import React, { useState, useEffect, useRef } from 'react';
import { sendEmail } from './emailService.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Icon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const assessmentData = {
  title: "The Leader's Lens: Map or Territory?",
  introduction: "This assessment is designed to help you understand your leadership style. It explores whether you tend to lead based on your internal 'map' (assumptions, plans, past experiences) or the 'territory' (the current, evolving reality). There are no right or wrong answers, only opportunities for insight.",
  questions: [
    {
      id: 1,
      scenario: "A key project, which you meticulously planned, is falling behind schedule. Your initial data suggested the timeline was feasible. The team reports that unforeseen technical hurdles are the cause.",
      question: "What is your most likely immediate action?",
      options: [
        { text: "Re-examine the original plan to find where the team is deviating from it.", score: 1 },
        { text: "Call a meeting to brainstorm solutions, trusting the team's assessment of the new hurdles.", score: 4 },
        { text: "Ask the team for a detailed report on the hurdles, comparing it against your initial risk assessment.", score: 2 },
        { text: "Push the team to work harder to meet the original deadline, believing the hurdles are manageable.", score: 0 },
      ],
    },
    {
      id: 2,
      scenario: "You're hiring for a critical role. A candidate has the perfect resume and nails all the standard interview questions. However, you have a nagging feeling that they aren't a good cultural fit.",
      question: "How do you proceed?",
      options: [
        { text: "Ignore the feeling. The candidate's qualifications (the map) are what's important for the role.", score: 0 },
        { text: "Arrange a casual team lunch with the candidate to observe their interactions (explore the territory).", score: 4 },
        { text: "Ask them more pointed, unconventional questions to try and validate or disprove your feeling.", score: 3 },
        { text: "Trust your gut feeling entirely and pass on the candidate without further investigation.", score: 1 },
      ],
    },
    {
      id: 3,
      scenario: "A long-standing, previously successful company policy is now receiving consistent complaints from your top-performing employees. They argue it's outdated and hindering their productivity.",
      question: "What's your response?",
      options: [
        { text: "Defend the policy. It has worked for years, and the data proves its historical effectiveness.", score: 0 },
        { text: "Acknowledge their feedback and promise to 'look into it' when you have time.", score: 1 },
        { text: "Form a small task force of these employees to analyze the policy's current impact and propose alternatives.", score: 4 },
        { text: "Remind the employees of the reasons the policy was created in the first place.", score: 2 },
      ],
    },
    {
        id: 4,
        scenario: "During a performance review, an employee you've always rated as 'average' presents a portfolio of self-initiated projects that are innovative and highly successful. This contradicts your long-held assessment of them.",
        question: "How does this new information affect your review?",
        options: [
            { text: "Acknowledge the projects, but maintain your overall 'average' rating, viewing this as a recent, uncharacteristic surge.", score: 1 },
            { text: "Question the employee on why they didn't show this initiative before, feeling slightly skeptical.", score: 2 },
            { text: "Fundamentally re-evaluate your entire perception of the employee, admitting your previous 'map' of them was incomplete.", score: 4 },
            { text: "Praise the projects but suggest they focus more on their core, assigned duties where they are average.", score: 0 },
        ],
    },
    {
        id: 5,
        scenario: "Your team is split 50/50 on two different strategies for a major product launch. Your experience strongly favors Strategy A, which has worked for you in the past.",
        question: "How do you resolve the deadlock?",
        options: [
            { text: "Make the final call for Strategy A, citing your experience and its past success as the deciding factor.", score: 1 },
            { text: "Allow both factions to present their case, but ultimately guide the conversation towards the merits of Strategy A.", score: 2 },
            { text: "Propose a low-cost way to test key assumptions from both strategies in the real market before committing fully.", score: 4 },
            { text: "Implement Strategy A, but promise the other half of the team they can use their strategy on the 'next one'.", score: 0 },
        ],
    },
  ],
  results: [
    {
      score: 0,
      title: "The Rigid Mapper (0-8 points)",
      description: "Your leadership style appears to rely heavily on your internal 'map' – your plans, beliefs, and past experiences. You trust your map implicitly, sometimes to the point of ignoring conflicting information from the 'territory'.",
      advice: [
        "Practice active listening. Intentionally seek to understand, not just to reply.",
        "Before making a decision, ask yourself: 'What am I assuming to be true here?'",
        "Introduce 'reality checks' into your projects. Create formal points to stop and ask, 'Is our plan still matching the reality on the ground?'",
        "Seek out dissenting opinions. Reward team members who challenge your assumptions respectfully."
      ]
    },
    {
      score: 9,
      title: "The Cautious Surveyor (9-14 points)",
      description: "You understand the difference between the map and the territory, but you are often hesitant to deviate from the map. You notice when reality doesn't match the plan, but your instinct is to try and make reality fit the plan, rather than updating the plan.",
      advice: [
        "Embrace small experiments. When faced with uncertainty, don't just analyze – test.",
        "Develop 'what if' scenarios. Actively consider how you would respond if your primary plan fails.",
        "When you get a 'gut feeling', don't dismiss it. Instead, get curious. Ask 'What data is my intuition processing that I'm not consciously aware of?'",
        "Delegate decisions to those closest to the information. This empowers them and gives you access to a more accurate view of the territory."
      ]
    },
    {
      score: 15,
      title: "The Flexible Navigator (15-20 points)",
      description: "You have a strong ability to navigate the complexities of leadership. You create a good map but you hold it loosely, readily updating it with new information from the territory. You see your plans as a guide, not a gospel.",
      advice: [
        "Continue to hone your skills in facilitating psychological safety, so your team always feels safe bringing you fresh data from the territory.",
        "Mentor others in this skill. Help your emerging leaders learn to distinguish between their assumptions and reality.",
        "Systematize your flexibility. How can you build processes that encourage adaptation, not just rely on your personal style?",
        "Challenge your own successful models. The map that got you here might not get you to the next level. What are its current limitations?"
      ]
    }
  ]
};
  

const WelcomeScreen = ({ onStart, title, introduction }) => (
    <div className="w-full flex flex-col items-center justify-center p-8 md:p-12">
        <Icon className="w-16 h-16 text-indigo-500" />
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mt-4 text-center">{title}</h1>
        <p className="mt-4 text-lg text-gray-600 max-w-2xl text-center">
            {introduction}
        </p>
        <button
            onClick={onStart}
            className="mt-8 px-8 py-4 bg-indigo-600 text-white font-bold rounded-lg shadow-lg hover:bg-indigo-700 transform hover:scale-105 transition-all"
        >
            Start Assessment
        </button>
    </div>
);

const Question = ({ question, onAnswer, questionNumber, totalQuestions }) => (
    <div className="p-8 md:p-12 w-full">
        <p className="text-sm text-gray-500">Question {questionNumber} of {totalQuestions}</p>
        <p className="text-lg text-gray-600 mt-4 italic">Scenario: {question.scenario}</p>
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mt-2">{question.question}</h2>
        <div className="mt-8 space-y-4">
            {question.options.map((option, index) => (
                <button
                    key={index}
                    onClick={() => onAnswer(option.score)}
                    className="w-full text-left p-4 bg-gray-100 rounded-lg hover:bg-indigo-100 border-2 border-transparent focus:border-indigo-500 focus:outline-none transition-colors"
                >
                    {option.text}
                </button>
            ))}
        </div>
    </div>
);

// --- NEW Email Collection Component ---
// Place this component definition with the others in your App.js file

const EmailCollectionScreen = ({ onStart }) => {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Basic email validation
    if (email && email.includes('@')) {
      onStart(email); // Pass the email up to the main App component
    } else {
      alert('Please enter a valid email address.');
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center p-8 md:p-12">
      <Icon className="w-16 h-16 text-indigo-500" />
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mt-4 text-center">Enter Your Email</h1>
      <p className="mt-2 text-lg text-gray-600 max-w-2xl text-center">
        Your results will be automatically emailed to you upon completion.
      </p>
      <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-2 w-full max-w-md">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.email@example.com"
          className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
          required
        />
        <button
          type="submit"
          className="w-full px-8 py-3 bg-indigo-600 text-white font-bold rounded-lg shadow-lg hover:bg-indigo-700 transform hover:scale-105 transition-all"
        >
          Start Assessment
        </button>
      </form>
    </div>
  );
};

// --- UPDATED ResultsScreen Component (Now with automatic sending) ---
// Note the changes: it now receives the participant's email as a prop
// and uses the useEffect hook to send the email automatically.

const ResultsScreen = ({ score, onRestart, participantEmail }) => {
  const result = assessmentData.results.slice().reverse().find(r => score >= r.score);
  const resultsRef = useRef();
  const [emailStatus, setEmailStatus] = useState('sending'); // Start in 'sending' state

useEffect(() => {
    if (!participantEmail) {
        setEmailStatus('error');
        console.error("Email sending failed: No participant email provided.");
        return;
    }

    const fullResultText = `
Result Title: ${result.title}

Description: ${result.description}

Actionable Advice for Growth:
- ${result.advice.join('\n- ')}
    `;

    const templateParams = {
      to_email: participantEmail,
      results_as_text: fullResultText,
    };

    // Make sure this line includes setEmailStatus
    sendEmail(templateParams, "Your Leader's Lens Assessment Results", setEmailStatus);

}, [participantEmail, result]);

  return (
    <div className="w-full">
      {/* This div is what gets turned into a PDF */}
      <div ref={resultsRef} className="text-center p-8 md:p-12">
        <h2 className="text-2xl font-bold text-gray-500">Your Result</h2>
        <p className="text-5xl md:text-6xl font-bold text-indigo-600 my-4">{result.title}</p>
        <div className="max-w-3xl mx-auto">
          <p className="text-lg text-gray-700 mb-6">{result.description}</p>
          <div className="bg-gray-50 rounded-lg p-6 text-left">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Actionable Advice for Growth:</h3>
            <ul className="space-y-3">
              {result.advice.map((item, index) => (
                <li key={index} className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  <span className="text-gray-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      
      {/* --- NEW AUTOMATIC EMAIL STATUS SECTION --- */}
      <div className="text-center pb-8">
        {emailStatus === 'sending' && <p className="text-gray-600 mt-4">Sending your PDF results...</p>}
        {emailStatus === 'sent' && <p className="text-green-600 mt-4">✅ Results have been sent to your email!</p>}
        {emailStatus === 'error' && <p className="text-red-600 mt-4">❌ There was an error sending your results.</p>}
      </div>
      {/* --- END OF NEW SECTION --- */}

      <div className="text-center pb-10">
        <button
          onClick={onRestart}
          className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-lg shadow-lg hover:bg-indigo-700"
        >
          Take Again
        </button>
      </div>
    </div>
  );
};


// --- UPDATED Main App Component ---
function LeadershipAssessment() {
  // Add 'collectingEmail' to the possible states
  const [gameState, setGameState] = useState('welcome'); // 'welcome', 'collectingEmail', 'playing', 'results'
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [participantEmail, setParticipantEmail] = useState(''); // State to hold the email

  const handleProceedToEmail = () => {
    setGameState('collectingEmail');
  };

  const handleStartAssessment = (email) => {
    setParticipantEmail(email);
    setGameState('playing');
    setCurrentQuestionIndex(0);
    setScore(0);
  };
  
  const handleRestart = () => {
    setGameState('welcome');
    setParticipantEmail('');
    setCurrentQuestionIndex(0);
    setScore(0);
  }

  const handleAnswer = (answerScore) => {
    setScore(prevScore => prevScore + answerScore);
    if (currentQuestionIndex < assessmentData.questions.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    } else {
      setGameState('results');
    }
  };
  
  const totalQuestions = assessmentData.questions.length;
  const progress = ((currentQuestionIndex) / totalQuestions) * 100;

  return (
    <div className="bg-gray-100 min-h-screen font-sans flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-500 ease-in-out">
        {gameState === 'playing' && (
          <div className="w-full bg-gray-200 h-2.5">
            <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${progress}%`, transition: 'width 0.5s ease-in-out' }}></div>
          </div>
        )}
        
        <div className="min-h-[600px] flex items-center">
            {gameState === 'welcome' && <WelcomeScreen onStart={handleProceedToEmail} title={assessmentData.title} introduction={assessmentData.introduction} />}
            {gameState === 'collectingEmail' && <EmailCollectionScreen onStart={handleStartAssessment} />}
            {gameState === 'playing' && (
              <Question
                question={assessmentData.questions[currentQuestionIndex]}
                onAnswer={handleAnswer}
                questionNumber={currentQuestionIndex + 1}
                totalQuestions={totalQuestions}
              />
            )}
            {gameState === 'results' && (
              <ResultsScreen 
                score={score} 
                onRestart={handleRestart} 
                participantEmail={participantEmail} 
              />
            )}
        </div>

        <footer className="text-center text-xs text-gray-400 p-4 border-t border-gray-200">
          NLP Leadership Assessment &copy; 2024
        </footer>
      </div>
    </div>
  );
}

export default LeadershipAssessment;